<template>
  <div
    class="mission-ranking-share-section"
    :class="{ 'mission-ranking-share-section--for-pc': isPc }"
  >
    <div
      class="mission-ranking-share-section__container"
      :class="{ 'mission-ranking-share-section__container--wide': isWideDevice }"
    >
      <div
        class="mission-ranking-share-section__card"
        :style="backgroundImagePathStyle"
        :class="`mission-ranking-share-section__card--type-${shareRankingImageInfo.ranking.type.toLowerCase()}`"
      >
        <div class="mission-ranking-share-section__card-inner">
          <div class="mission-ranking-share-section__user">
            <p class="mission-ranking-share-section__user-name">
              {{ shareRankingImageInfo.displayName }}
            </p>
            <p class="mission-ranking-share-section__user-point">
              {{ shareRankingImageInfo.point }}{{ $tc('MissionPage.RankingPage.Share.point') }}
            </p>
          </div>
          <div class="mission-ranking-share-section__rank">
            <div
              v-if="shareRankingImageInfo.ranking.rank < 4"
              class="mission-ranking-share-section__rank-image"
              :class="`mission-ranking-share-section__rank-image${shareRankingImageInfo.ranking.rank}`"
            >
              <p class="mission-ranking-share-section__rank-number">
                {{ shareRankingImageInfo.ranking.rank }}
              </p>
            </div>

            <div class="mission-ranking-share-section__rank-display" v-else>
              <p class="mission-ranking-share-section__rank-display-label">
                {{ $tc('MissionPage.RankingPage.Share.rank') }}
              </p>
              <p
                class="mission-ranking-share-section__rank-display-value"
                :class="{
                  'mission-ranking-share-section__rank-display-value--over':
                    shareRankingImageInfo.ranking.rank > 99999,
                }"
              >
                {{ shareRankingImageInfo.ranking.rank }}
              </p>
            </div>

            <div class="mission-ranking-share-section__rank-body">
              <p class="mission-ranking-share-section__rank-title">
                {{ shareRankingImageInfo.ranking.headline }}
              </p>
              <p class="mission-ranking-share-section__rank-name">
                {{ shareRankingImageInfo.ranking.title }}
              </p>
              <p class="mission-ranking-share-section__rank-date">
                <span>{{ shareRankingImageInfo.ranking.mothDate }}</span>
              </p>
            </div>
          </div>
          <p class="mission-ranking-share-section__date">{{ today }}</p>
        </div>
        <div class="mission-ranking-share-section__card-overlay"></div>
        <div class="mission-ranking-share-section__card-decoration"></div>
      </div>

      <div class="mission-ranking-share-section__footer">
        <p v-if="isPc" class="mission-ranking-share-section__footer-note">
          {{ $tc('MissionPage.RankingPage.Share.noteOnlyX') }}
        </p>
        <template v-if="!isPc && !visiblePostSnsModal">
          <ul class="mission-ranking-share-section__buttons">
            <li class="mission-ranking-share-section__buttons-item">
              <atom-button
                class="mission-ranking-share-section__button"
                :theme="'dark'"
                :colorType="'secondary'"
                :linkText="$tc('MissionPage.RankingPage.Share.postOnlyX')"
                @onClick="openSnsPostModal"
              />
            </li>
            <li class="mission-ranking-share-section__buttons-item">
              <atom-button
                class="mission-ranking-share-section__button"
                :theme="'dark'"
                :colorType="'tertiary'"
                :linkText="$tc('MissionPage.RankingPage.Share.download')"
                @onClick="handleImageDownloadClicked"
              />
            </li>
          </ul>
        </template>
        <ul v-else-if="isPc" class="mission-ranking-share-section__buttons">
          <li class="mission-ranking-share-section__buttons-item">
            <atom-button
              class="mission-ranking-share-section__button"
              :theme="'dark'"
              :linkText="$tc('MissionPage.RankingPage.Share.download')"
              @onClick="handleImageDownloadClicked"
            />
          </li>
          <li class="mission-ranking-share-section__buttons-item">
            <atom-button
              class="mission-ranking-share-section__button"
              :theme="'dark'"
              :colorType="'secondary'"
              :linkText="$tc('MissionPage.RankingPage.Share.postX')"
              @onClick="onClickAnchorLink(xUrl)"
            />
          </li>
          <li v-if="false" class="mission-ranking-share-section__buttons-item">
            <atom-button
              class="mission-ranking-share-section__button"
              :theme="'dark'"
              :colorType="'secondary'"
              :linkText="$tc('MissionPage.RankingPage.Share.postInstagram')"
              @onClick="onClickAnchorLink(instagramUrl)"
            />
          </li>
        </ul>
        <p v-if="!visiblePostSnsModal" class="mission-ranking-share-section__submit">
          <atom-button
            class="mission-ranking-share-section__button"
            :theme="'dark'"
            :linkText="$tc('common.ok')"
            @onClick="$emit('onClickOk')"
          />
        </p>
      </div>
    </div>

    <!-- 入力ポップアップ -->
    <div class="mission-ranking-share-section__post" v-if="visiblePostSnsModal">
      <MissionRankingSharePostSection
        :imageUrl="shareImageUrl"
        @onClickCancel="onClickCancel"
        @onClickPostSns="onClickPostSns"
      />
    </div>
  </div>
</template>

<script lang="ts">
import dayjs from 'dayjs'
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
} from '@vue/composition-api'
import AtomButton from '@/components/atoms/AtomButton.vue'
import MissionRankingSharePostSection from '@/components/MissionPage/MissionRankingPane/MissionRankingSharePostSection.vue'
import DeviceInfo from '@/util/DeviceInfo'
import {
  RankingUserImageDataType,
  ShareRankingImageType,
  RankingImageInfoType,
} from '@/store/stores/pageStore/MissionPage/MissionPageStore'
import useDisplayDependingOnLang from '@/components/hook/useDisplayDependingOnLang'
import Const from '@/util/Const'

type ImagePathStyleType = {
  backgroundImage: string
}

export default defineComponent({
  name: 'MissionRankingShareSection',
  components: { MissionRankingSharePostSection, AtomButton },
  props: {
    shareImageUrl: {
      type: String,
      default: '',
    },
    backgroundImage: {
      type: Object as PropType<RankingImageInfoType | null | undefined>,
      default: () => ({}),
    },
    backgroundImagePath: {
      type: String,
      default: '',
    },
    visiblePostSnsModal: {
      type: Boolean,
      default: false,
    },
    rankingUserImageData: {
      type: Object as PropType<RankingUserImageDataType>,
      default: () => ({}),
    },
    shareRankingImageInfo: {
      type: Object as PropType<ShareRankingImageType>,
      default: () => ({}),
    },
  },
  setup(props, context) {
    const { getDisplayDateJa } = useDisplayDependingOnLang()

    // XのURL
    const xUrl = Const.EXTERNAL_LINKS.X.HOME
    // InstagramのURL
    const instagramUrl = Const.EXTERNAL_LINKS.INSTAGRAM.HOME

    const backgroundImagePathStyle = computed(
      () =>
        ({
          backgroundImage: `url(${props.backgroundImagePath})`,
        } as ImagePathStyleType),
    )

    const isPc = DeviceInfo.isForPC()

    // 表示日時
    const today = getDisplayDateJa(dayjs().valueOf(), 'YYYY / MM / DD')

    // X, Instagramに投稿するが押下されたときの処理
    const openSnsPostModal = async () => {
      context.emit('openSnsPostModal', today)
    }

    /**
     * SNS投稿ボタンをクリックした場合に呼び出される。
     * @param url 外部サイトのリンク
     */
    const onClickAnchorLink = (url: string) => {
      // 外部リンクを開く
      window.open(url, '_blank')
      context.emit('savePostSnsMissionHistory')
    }

    // 画像をダウンロードするが押下されたときの処理
    const handleImageDownloadClicked = async () => {
      context.emit('handleImageDownloadClicked', today)
    }

    const isWideDevice = ref(false)

    const onClickCancel = () => {
      context.emit('onClickCancel')
    }

    const onClickPostSns = async (
      base64: string,
      comment: string,
      isPostX: boolean,
      isPostInstagram: boolean,
    ) => {
      context.emit('onClickPostSns', base64, comment, isPostX, isPostInstagram)
    }

    const checkDeviceWide = () => {
      isWideDevice.value = window.innerHeight > 700
    }

    onMounted(() => {
      checkDeviceWide()
      window.addEventListener('resize', checkDeviceWide)
    })

    onBeforeUnmount(() => {
      window.addEventListener('resize', checkDeviceWide)
    })

    return {
      backgroundImagePathStyle,
      today,
      handleImageDownloadClicked,
      openSnsPostModal,
      isWideDevice,
      isPc,
      onClickAnchorLink,
      xUrl,
      instagramUrl,
      onClickCancel,
      onClickPostSns,
    }
  },
})
</script>

<style scoped lang="scss">
.mission-ranking-share-section {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  padding: 8px;
  overflow: auto;

  &::before {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    content: '';
    background-color: $black70;
    backdrop-filter: blur(7px);
  }

  &--for-pc {
    padding-top: 68px;
    top: 48px;

    &::before {
      top: 48px;
    }
  }

  &__container {
    width: 100%;
    margin: auto;

    &--wide {
      z-index: 1;
      transform: translateY(3%);
    }

    .mission-ranking-share-section--for-pc & {
      z-index: 1;
      margin: 0;
      transform: translateY(0);
    }
  }

  &__card {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 360px;
    height: 360px;
    margin: auto;
    overflow: hidden;
    background-color: $black;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    border-radius: 4px;
    box-shadow: 0 0 60px 0 #ffffff80;

    &--type {
      &-total {
        color: #73ffdd;
      }

      &-season {
        color: #ebe553;
      }

      &-monthly {
        color: #ff5d5d;
      }
    }

    &-inner {
      position: relative;
      z-index: 3;
      height: 100%;
      padding: 2px 8px;
    }

    &-overlay {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      width: 100%;
      height: 100%;
      background-color: rgba(#000, 0.6);
    }

    &-decoration {
      position: absolute;
      bottom: 0;
      left: 0;
      z-index: 2;
      width: 100%;
      height: 123px;
      background-image: url('@/assets/img/MissionPage/ranking/bg_share_decoration.svg');
      background-position: left bottom;
      background-repeat: no-repeat;
      background-size: cover;
    }
  }

  &__user {
    color: $white70;
    text-align: right;

    &-name {
      font-size: 12px;
      font-weight: bold;
      line-height: 1.67;
      letter-spacing: 0.02em;
    }

    &-point {
      font-family: $fontHeading;
      font-size: 18px;
      font-weight: bold;
      letter-spacing: 0.04em;
      line-height: 1.3;
    }
  }

  &__rank {
    position: absolute;
    left: 3px;
    bottom: 6px;

    &-image {
      position: absolute;
      top: -149px;
      left: 0;
      display: flex;
      align-items: center;
      width: 166px;
      height: 157px;
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;

      &1 {
        justify-content: center;
        background-image: url('@/assets/img/MissionPage/ranking/bg_ranking_first.png');
      }

      &2 {
        justify-content: center;
        background-image: url('@/assets/img/MissionPage/ranking/bg_ranking_second.png');
      }

      &3 {
        justify-content: center;
        background-image: url('@/assets/img/MissionPage/ranking/bg_ranking_third.png');
      }
    }

    &-display {
      padding-left: 19px;
      margin-bottom: 14px;

      &-label {
        font-family: $fontHeading;
        font-size: 22px;
        font-weight: 600;
        line-height: 1.1;
      }

      &-value {
        font-family: $fontHeading;
        font-size: 62px;
        font-weight: 600;
        line-height: 1.2;
        letter-spacing: 0.04em;

        &--over {
          font-size: 52px;
          letter-spacing: normal;
        }
      }
    }

    &-number {
      font-family: $fontHeading;
      font-size: 58px;
      font-weight: 600;
      line-height: 1.1;
      transform: translateY(4px);
    }

    &-body {
      padding-left: 16px;
    }

    &-title {
      font-family: $fontHeading;
      font-size: 28px;
      font-weight: bold;
      letter-spacing: 0.04em;
      line-height: 1;
    }

    &-name {
      font-size: 17px;
      font-weight: bold;
      letter-spacing: 0.04em;
      line-height: 1.4;
    }

    &-date {
      height: 12px;
      font-size: 10px;
      font-weight: bold;
      letter-spacing: 0.04em;
      line-height: 1.2;
    }
  }

  &__date {
    position: absolute;
    right: 6px;
    bottom: 6px;
    font-size: 10px;
    letter-spacing: 0.04em;
  }

  &__footer {
    position: relative;
    z-index: 2;
    margin-top: 24px;

    &-note {
      width: 347px;
      margin: 0 auto 12px;
      font-family: $fontSignup;
      font-size: 13px;
      font-weight: 500;
      color: $white70;
      letter-spacing: 0.04em;
      text-align: center;
    }
  }

  &__buttons-item {
    + .mission-ranking-share-section__buttons-item {
      margin-top: 12px;
    }
  }

  &__button {
    width: 100%;
    max-width: 347px;
    margin-right: auto;
    margin-left: auto;
  }

  &__submit {
    margin-top: 24px;
  }

  &__post {
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 10;
    width: 100%;
    height: 100%;
  }
}
</style>
